import { Button } from "devextreme-react";
import { ButtonItem, Form, FormRef, GroupItem, Item, RequiredRule } from "devextreme-react/form";
import { LookupTypes } from "devextreme-react/lookup";
import { Popup } from "devextreme-react/popup";
import { format } from "date-fns";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/auth";
import { RoleEnum } from "../../../types";
import { useScreenSize } from "../../../utils/media-query";
import type { AbsenceNameOption2, UserForSelect2, ViewMode2 } from "../types";

interface AbsenceCreatePopup2Props {
  visible: boolean;
  onHiding: () => void;
  onSubmit: (employeeId: number) => void;
  viewMode: ViewMode2;
  isNotEmployee: boolean;
  isForeman: boolean;
  isTimeManager: boolean;
  users: UserForSelect2[];
  names: AbsenceNameOption2[];
  selectedDate: Date | undefined;
  setSelectedDate: (d: Date) => void;
  selectedEndDate: Date | undefined;
  setSelectedEndDate: (d: Date) => void;
  absenceName: string;
  setAbsenceName: (n: string) => void;
  notes: string;
  setNotes: (n: string) => void;
  skipDaysOff: boolean;
  setSkipDaysOff: (v: boolean) => void;
  leaveForAssignment: number;
  setLeaveForAssignment: (v: number) => void;
  userSend: number[];
  setUserSend: (v: number[]) => void;
  entriesEmployeeId: number | undefined;
  onlyWeekendDays: boolean;
  cancelAbsenceData: number | undefined;
  cancelRequestPending: boolean;
  onCancelAbsence: (id: number) => void;
  isSubmitting: boolean;
  editableAbsenceId?: number;
  onDeleteOwnAbsence?: (id: number) => void;
  hourFrom: string | null;
  setHourFrom: (v: string | null) => void;
  hourTo: string | null;
  setHourTo: (v: string | null) => void;
  isHourlyAbsence: boolean;
  canBeHourly: boolean;
  addByHour: boolean;
  setAddByHour: (v: boolean) => void;
}

export function AbsenceCreatePopup2({
  visible,
  onHiding,
  onSubmit,
  viewMode,
  isNotEmployee,
  isForeman,
  isTimeManager,
  users,
  names,
  selectedDate,
  setSelectedDate,
  selectedEndDate,
  setSelectedEndDate,
  absenceName,
  setAbsenceName,
  notes,
  setNotes,
  skipDaysOff,
  setSkipDaysOff,
  leaveForAssignment,
  setLeaveForAssignment,
  userSend,
  setUserSend,
  entriesEmployeeId,
  onlyWeekendDays,
  cancelAbsenceData,
  cancelRequestPending,
  onCancelAbsence,
  isSubmitting,
  editableAbsenceId,
  onDeleteOwnAbsence,
  hourFrom,
  setHourFrom,
  hourTo,
  setHourTo,
  isHourlyAbsence,
  canBeHourly,
  addByHour,
  setAddByHour,
}: AbsenceCreatePopup2Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const formRef = useRef<FormRef | null>(null);
  const role = user?.roles;
  // Słowniki typów nieobecności różnią się między spółkami (np. krwiodawstwo to "Krew - oddanie krwi"
  // w INVENIO QD, a "Dni wolne z tyt. krwiodawstwa" w pozostałych), a nieobecność zapisywana jest do
  // Optimy spółki PRACOWNIKA. Przy wypożyczeniu kierownik zgłasza za pracownika z innej spółki, więc
  // lista musi być zawężona do typów istniejących we WSZYSTKICH spółkach wskazanych pracowników.
  const targetCompanyIds = useMemo(() => {
    const ids =
      userSend.length > 0
        ? userSend.map((employeeId) => users.find((u) => u.id === employeeId)?.companyId)
        : entriesEmployeeId !== undefined
          ? [users.find((u) => u.id === entriesEmployeeId)?.companyId]
          : [];

    const resolved = ids.filter((id): id is number => typeof id === "number" && id !== 0);

    return resolved.length > 0 ? Array.from(new Set(resolved)) : [user?.companyId ?? 0];
  }, [userSend, entriesEmployeeId, users, user?.companyId]);

  const filteredNames = useMemo(
    () =>
      names.filter(
        (n) =>
          !n.companyIds ||
          n.companyIds.length === 0 ||
          targetCompanyIds.every((companyId) => n.companyIds!.includes(companyId)),
      ),
    [names, targetCompanyIds],
  );

  const showEmployeeSelector =
    (viewMode === "AllEmployees" && (isNotEmployee || isTimeManager) && !isForeman) ||
    (viewMode === "Employee" &&
      ((isNotEmployee && !role?.includes(RoleEnum.Kierownik) && !role?.includes(RoleEnum.Manager)) || isTimeManager) &&
      !isForeman);

  return (
    <Popup
      className="popup-ava"
      visible={visible}
      onHiding={onHiding}
      fullScreen={isMobile}
      maxWidth={isMobile ? undefined : 500}
      height={isMobile ? "100%" : "auto"}
      maxHeight={isMobile ? undefined : "80vh"}
      title={t("holidayPopupChoose.chooseYourHoliday")}
      showCloseButton
      dragEnabled={!isMobile}
      deferRendering={false}
      animation={{
        show: { type: "fade", duration: 150, from: 0, to: 1 },
        hide: { type: "fade", duration: 100, from: 1, to: 0 },
      }}
    >
      {onlyWeekendDays ? (
        <div className="p-4 mb-3 rounded-md border-yellow-500 bg-yellow-50 text-yellow-800 text-sm text-justify">
          {t("holidayPopupChoose.onlyWeekendDaysAlert")}
        </div>
      ) : null}

      {cancelAbsenceData ? (
        <div className="p-4 mb-3 flex flex-col gap-3 rounded-md border-yellow-500 bg-yellow-50 text-yellow-800 text-sm text-justify">
          <div>{t("holidayPopup.thisHolidayCanOnlyBeCancelledAsItHasAlreadyBeenSyncedWithOptima")}</div>
          <Button
            type="danger"
            onClick={() => onCancelAbsence(cancelAbsenceData)}
            disabled={cancelRequestPending}
          >
            {t("holidayPopup.cancelLeave")}
          </Button>
        </div>
      ) : null}

      {editableAbsenceId ? (
        <div className="p-4 mb-3 rounded-md border-yellow-500 bg-yellow-50 text-yellow-800 text-sm text-justify">
          {t("holidayPopup.editingNotAllowed")}
        </div>
      ) : null}

      <form className="holiday-form">
        <Form ref={formRef} disabled={!!cancelAbsenceData || !!editableAbsenceId}>
          {showEmployeeSelector && (
            <Item
              dataField={t("availabilityPopup.employee")}
              editorType="dxTagBox"
              editorOptions={{
                items: users,
                showSelectionControls: true,
                searchEnabled: true,
                applyValueMode: "useButtons",
                showClearButton: true,
                displayExpr: (u: UserForSelect2) => (u ? `${u.lastName} ${u.firstName}` : ""),
                valueExpr: "id",
                value: userSend || [],
                onValueChanged: (e: { value: number[] }) => setUserSend(e.value || []),
                placeholder: t("availabilityPopup.selectEmployee"),
                dropDownOptions: { showScrollbar: "onHover", hideOnOutsideClick: true },
              }}
            >
              <RequiredRule />
            </Item>
          )}

          <Item
            dataField={t("holidayPopupChoose.selectName")}
            editorType="dxLookup"
            editorOptions={{
              items: filteredNames,
              displayExpr: "name",
              valueExpr: "name",
              value: absenceName,
              onValueChanged: (e: { value: string }) => setAbsenceName(e.value),
              placeholder: t("holidayPopupChoose.nameOfTheHoliday"),
              dropDownOptions: { showScrollbar: "onHover", hideOnOutsideClick: true, showTitle: false },
            } as LookupTypes.Properties}
          >
            <RequiredRule />
          </Item>

          <Item
            dataField={t("holidayPopupChoose.holidayOnRequest")}
            editorType="dxCheckBox"
            editorOptions={{
              value: leaveForAssignment,
              onValueChanged: (e: { value: number }) => setLeaveForAssignment(e.value),
              text: t("holidayPopupChoose.holidayOnRequest"),
            }}
            label={{ visible: false }}
          />

          {canBeHourly && (
            <Item
              dataField={t("holidayPopupChoose.addByHour")}
              editorType="dxCheckBox"
              editorOptions={{
                value: addByHour,
                onValueChanged: (e: { value: boolean }) => setAddByHour(e.value),
                text: t("holidayPopupChoose.addByHour"),
              }}
              label={{ visible: false }}
            />
          )}

          <GroupItem colCount={isMobile ? 1 : 2}>
            <Item
              dataField={t("holidayPopupChoose.startDate")}
              editorType="dxDateBox"
              editorOptions={{
                value: selectedDate,
                type: "date",
                pickerType: isMobile ? "rollers" : "calendar",
                onValueChanged: (e: { value: Date }) => setSelectedDate(e.value),
              }}
            >
              <RequiredRule />
            </Item>
            <Item
              dataField={t("holidayPopupChoose.endDate")}
              editorType="dxDateBox"
              editorOptions={{
                value: selectedEndDate,
                type: "date",
                pickerType: isMobile ? "rollers" : "calendar",
                disabled: isHourlyAbsence,
                onValueChanged: (e: { value: Date }) => setSelectedEndDate(e.value),
              }}
            >
              <RequiredRule />
            </Item>
          </GroupItem>

          {isHourlyAbsence && (
            <GroupItem colCount={isMobile ? 1 : 2}>
              <Item
                dataField={t("holidayPopupChoose.startHour")}
                editorType="dxDateBox"
                editorOptions={{
                  value: hourFrom ? new Date(`1970-01-01T${hourFrom}`) : null,
                  type: "time",
                  pickerType: isMobile ? "rollers" : "list",
                  interval: 15,
                  onValueChanged: (e: { value: Date | null }) =>
                    setHourFrom(e.value ? format(e.value, "HH:mm:ss") : null),
                }}
              >
                <RequiredRule />
              </Item>
              <Item
                dataField={t("holidayPopupChoose.endHour")}
                editorType="dxDateBox"
                editorOptions={{
                  value: hourTo ? new Date(`1970-01-01T${hourTo}`) : null,
                  type: "time",
                  pickerType: isMobile ? "rollers" : "list",
                  interval: 15,
                  onValueChanged: (e: { value: Date | null }) =>
                    setHourTo(e.value ? format(e.value, "HH:mm:ss") : null),
                }}
              >
                <RequiredRule />
              </Item>
            </GroupItem>
          )}

          <Item
            dataField={t("holidayPopupChoose.notes")}
            editorType="dxTextArea"
            editorOptions={{
              value: notes,
              width: "100%",
              height: 75,
              onValueChanged: (e: { value: string }) => setNotes(e.value),
            }}
          />

          <GroupItem colCount={2}>
            <Item
              dataField={t("presencePopup.skipDaysOff")}
              editorType="dxCheckBox"
              editorOptions={{
                value: skipDaysOff,
                onValueChanged: (e: { value: boolean }) => setSkipDaysOff(e.value),
                text: t("presencePopup.skipDaysOff"),
              }}
              label={{ visible: false }}
            />
          </GroupItem>

          {!editableAbsenceId && (
            <ButtonItem
              horizontalAlignment={isMobile ? "center" : "right"}
              buttonOptions={{
                text: t("Send"),
                type: "default",
                width: isMobile ? "100%" : undefined,
                disabled: isSubmitting || onlyWeekendDays,
                onClick: () => {
                  const validationResult = formRef.current?.instance().validate();
                  if (validationResult?.isValid) {
                    if (viewMode === "Employee") {
                      onSubmit(user?.employeeId || 0);
                    } else {
                      onSubmit(entriesEmployeeId || 0);
                    }
                  }
                },
              }}
            />
          )}
        </Form>

        {editableAbsenceId && (
          <div className={`flex gap-2 mt-3 ${isMobile ? "flex-col" : "justify-end"}`}>
            <Button
              type="danger"
              width={isMobile ? "100%" : undefined}
              disabled={isSubmitting}
              onClick={() => onDeleteOwnAbsence?.(editableAbsenceId)}
            >
              {t("holidayPopup.cancelLeave")}
            </Button>
          </div>
        )}
      </form>
    </Popup>
  );
}
